import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import ConfirmationModal from "src/components/ConfirmationModal";
import ToolTip from "src/components/ToolTip";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  SortDescriptor,
  CompositeFilterDescriptor,
  FilterDescriptor,
} from "@progress/kendo-data-query";
import ReactDOM from "react-dom";

// --- Interfaces ---

interface WarmupJob {
  warmupJobId: number;
  acctId: string;
  accountName: string | null;
  name: string;
  status: string;
  startDate: string;
  rampSchedule: string;
  carriers: string | null;
  callDurations: string | null;
  currentDay: number;
  totalDays: number;
  phoneCount: number;
  todaysTarget: number;
  todaysActual: number;
  createdBy: string;
  createdOn: string;
  modifiedOn: string;
}

interface WarmupJobNumber {
  warmupJobNumberId: number;
  phoneNumber: string;
  status: string;
  callsToday: number;
  totalCalls: number;
  lastCalledOn: string | null;
  addedOn: string;
}

interface WarmupCallLog {
  warmupCallLogId: number;
  phoneNumber: string;
  devicePhoneNumber: string;
  carrier: string;
  calledOn: string;
  callDurationSecs: number;
  asteriskResponse: string | null;
  success: boolean;
}

interface DailyForecast {
  date: string;
  projectedCalls: number;
  activeJobCount: number;
}

interface CarrierCapacity {
  carrier: string;
  /** Devices flagged reservedForWarmup in SIMFarm — the farm's actual supply. */
  provisionedDevices: number;
  /** Distinct devices that placed a call in the last 30 minutes. */
  activeDevices: number;
  callsPerMinute: number;
  /** Planned calls for today across in-progress jobs. */
  todaysDemand: number;
  /** Highest planned single-day volume over the next 7 days (ramps grow). */
  peakDailyDemand: number;
  /** Max calls/day the provisioned devices can place. */
  dailyCapacity: number;
  /** peakDailyDemand as % of dailyCapacity. 100+ = jobs can't finish on schedule. */
  utilizationPct: number;
  /** Service Bus messages due now but not yet dialed. Null when unavailable. */
  queueBacklog: number | null;
}

interface CapacitySummary {
  activeJobs: number;
  scheduledJobs: number;
  totalPhonesInWarmup: number;
  todaysTargetVolume: number;
  todaysActualVolume: number;
  activeDevices: number;
  callsPerMinute: number;
  totalCallsMade: number;
  remainingCalls: number;
  /** Ramp days left on the longest-running job — jobs finish on schedule, not on throughput. */
  rampDaysRemaining: number;
  forecast: DailyForecast[];
  byCarrier: CarrierCapacity[];
}

interface FilterState {
  acctId: string;
  status: string;
}

interface RampTemplate {
  label: string;
  value: number[];
  description: string;
}

// --- Constants ---

// Recipient of the "job completed" email unless the creator overrides it.
const DEFAULT_NOTIFICATION_EMAIL = "remediation@dnc.com";

const WARMUP_STATUSES = [
  { value: "Scheduled", label: "Scheduled", badge: "bg-warning text-dark" },
  { value: "InProgress", label: "In Progress", badge: "bg-primary" },
  { value: "Paused", label: "Paused", badge: "bg-secondary" },
  { value: "Completed", label: "Completed", badge: "bg-success" },
  { value: "Cancelled", label: "Cancelled", badge: "bg-danger" },
];

const RAMP_TEMPLATES: RampTemplate[] = [
  {
    label: "Flat (7 days)",
    value: [5, 5, 5, 5, 5, 5, 5],
    description:
      "7 days: 5 \u2192 5 \u2192 5 \u2192 5 \u2192 5 \u2192 5 \u2192 5",
  },
  {
    label: "Light (3 days)",
    value: [5, 10, 20],
    description: "3 days: 5 \u2192 10 \u2192 20",
  },
  {
    label: "Moderate (6 days)",
    value: [3, 5, 10, 15, 25, 40],
    description: "6 days: 3 \u2192 5 \u2192 10 \u2192 15 \u2192 25 \u2192 40",
  },
  {
    label: "Standard (9 days)",
    value: [3, 3, 5, 8, 10, 15, 20, 30, 40],
    description:
      "9 days: 3 \u2192 3 \u2192 5 \u2192 8 \u2192 10 \u2192 15 \u2192 20 \u2192 30 \u2192 40",
  },
  {
    label: "Extended (12 days)",
    value: [2, 3, 3, 5, 5, 8, 10, 10, 15, 20, 30, 40],
    description:
      "12 days: 2 \u2192 3 \u2192 3 \u2192 5 \u2192 5 \u2192 8 \u2192 10 \u2192 10 \u2192 15 \u2192 20 \u2192 30 \u2192 40",
  },
  { label: "Custom", value: [], description: "" },
];

const formatPhone = (phone: string): string => {
  if (!phone || phone.length !== 10) return phone;
  return `+1 ${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
};

const getStatusBadgeClass = (status: string): string => {
  return (
    WARMUP_STATUSES.find((s) => s.value === status)?.badge || "bg-secondary"
  );
};

// --- Loading Panel ---

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const buildFilterParams = (
  filter: CompositeFilterDescriptor | undefined,
  fields: string[],
): string => {
  if (!filter?.filters) return "";
  return fields
    .map((field) => {
      const f = filter.filters.find(
        (f) => (f as FilterDescriptor).field === field,
      ) as FilterDescriptor | undefined;
      if (!f || f.value == null || f.value === "") return "";
      const val =
        f.value instanceof Date ? f.value.toISOString() : String(f.value);
      const op = f.operator || "eq";
      return `&${field}=${encodeURIComponent(val)}&${field}Op=${encodeURIComponent(String(op))}`;
    })
    .join("");
};

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

// --- Helpers ---

const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return "-";
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-US", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "-";
  }
};

const parseRamp = (json: string): number[] => {
  try {
    return JSON.parse(json) || [];
  } catch {
    return [];
  }
};

const formatRampPreview = (ramp: number[]): string => {
  if (ramp.length === 0) return "";
  return `${ramp.length} days: ${ramp.join(" \u2192 ")}`;
};

const parsePhoneNumbers = (text: string): string[] => {
  const lines = text.split(/[\r\n]+/);
  const phones: string[] = [];
  for (const line of lines) {
    let digits = line.replace(/[^\d]/g, "");
    if (digits.length === 11 && digits.startsWith("1"))
      digits = digits.substring(1);
    if (digits.length === 10) phones.push(digits);
  }
  return [...new Set(phones)];
};

// --- Component ---

const WarmupJobs: React.FC = () => {
  const { jobId: urlJobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  // Grid state
  const [jobs, setJobs] = useState<WarmupJob[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 25 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "createdOn", dir: "desc" },
  ]);
  const [filters, setFilters] = useState<FilterState>({
    acctId: "",
    status: "",
  });
  const gridRef = useRef<HTMLDivElement>(null);

  // Capacity summary
  const [capacity, setCapacity] = useState<CapacitySummary | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    acctId: "",
    name: "",
    startDate: new Date(),
    rampTemplateIndex: 3, // Standard
    customRamp: "",
    carriers: ["Verizon"] as string[],
    callDurations: { ATT: 7, Verizon: 7, TMobile: 7 } as Record<string, number>,
    notificationEmail: DEFAULT_NOTIFICATION_EMAIL,
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPhoneCount, setCsvPhoneCount] = useState(0);
  const [accountNameLookup, setAccountNameLookup] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<WarmupJob | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    jobId: number;
    action: "pause" | "resume" | "cancel";
  } | null>(null);
  const [jobNumbers, setJobNumbers] = useState<WarmupJobNumber[]>([]);
  const [totalNumbers, setTotalNumbers] = useState(0);
  const [numbersPage, setNumbersPage] = useState({ skip: 0, take: 50 });
  const [numbersFilter, setNumbersFilter] = useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [callLogs, setCallLogs] = useState<WarmupCallLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [logsPage, setLogsPage] = useState({ skip: 0, take: 50 });
  const [logsFilter, setLogsFilter] = useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [detailTab, setDetailTab] = useState<"numbers" | "logs">("numbers");

  // --- Data Loading ---

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const currentSort = sort.length > 0 ? sort[0] : null;
      const response = await callApi(
        "sysadmin/warmup/jobs/list",
        HttpMethod.POST,
        {
          acctId: filters.acctId || null,
          status: filters.status || null,
          skip: page.skip,
          take: page.take,
          sortField: currentSort?.field || "createdOn",
          sortDir: currentSort?.dir || "desc",
        },
      );
      setJobs(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error("Error fetching warmup jobs:", err);
      setError(`Error fetching jobs: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCapacity = async () => {
    try {
      const response = await callApi(
        "sysadmin/warmup/capacity",
        HttpMethod.GET,
      );
      setCapacity(response);
    } catch (err: any) {
      console.error("Error fetching capacity summary:", err);
    }
  };

  const loadJobDetail = async (jobId: number) => {
    setIsLoadingDetail(true);
    try {
      const filterParams = buildFilterParams(numbersFilter, [
        "phoneNumber",
        "status",
        "callsToday",
        "totalCalls",
        "lastCalledOn",
      ]);
      const response = await callApi(
        `sysadmin/warmup/jobs/${jobId}?skip=${numbersPage.skip}&take=${numbersPage.take}${filterParams}`,
        HttpMethod.GET,
      );
      setSelectedJob(response.job);
      setJobNumbers(response.numbers || []);
      setTotalNumbers(response.totalNumbers || 0);
    } catch (err: any) {
      console.error("Error fetching job detail:", err);
      toast.error(`Error loading job detail: ${err.message || err}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const loadCallLogs = async (jobId: number) => {
    setIsLoadingLogs(true);
    try {
      const filterParams = buildFilterParams(logsFilter, [
        "phoneNumber",
        "devicePhoneNumber",
        "carrier",
        "success",
        "calledOn",
      ]);
      const response = await callApi(
        `sysadmin/warmup/jobs/${jobId}/logs?skip=${logsPage.skip}&take=${logsPage.take}${filterParams}`,
        HttpMethod.GET,
      );
      setCallLogs(response.data || []);
      setTotalLogs(response.total || 0);
    } catch (err: any) {
      console.error("Error fetching call logs:", err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    loadData();
  }, [page, sort, refreshKey]);

  useEffect(() => {
    loadCapacity();
  }, []);

  useEffect(() => {
    if (selectedJob) loadJobDetail(selectedJob.warmupJobId);
  }, [numbersPage, numbersFilter]);

  useEffect(() => {
    if (selectedJob) loadCallLogs(selectedJob.warmupJobId);
  }, [logsPage, logsFilter]);

  useEffect(() => {
    if (urlJobId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) {
        setShowDetailModal(true);
        loadJobDetail(id);
        loadCallLogs(id);
      }
    }
  }, [urlJobId]);

  // --- Handlers ---

  const handleSearch = () => {
    setPage({ skip: 0, take: page.take });
    setRefreshKey((k) => k + 1);
  };

  const handleReset = () => {
    setFilters({ acctId: "", status: "" });
    setPage({ skip: 0, take: 25 });
    setRefreshKey((k) => k + 1);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleViewJob = (jobId: number) => {
    setShowDetailModal(true);
    setDetailTab("numbers");
    setNumbersPage({ skip: 0, take: 50 });
    setLogsPage({ skip: 0, take: 50 });
    loadJobDetail(jobId);
    loadCallLogs(jobId);
    navigate(`/sysadmin/warmup-jobs/${jobId}`);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedJob(null);
    setJobNumbers([]);
    setCallLogs([]);
    navigate("/sysadmin/warmup-jobs");
  };

  const confirmConfig: Record<
    string,
    {
      title: string;
      html: string;
      confirmText: string;
      variant: "primary" | "danger" | "warning";
    }
  > = {
    pause: {
      title: "Pause Warmup Job?",
      html: "Calls in the queue will stop being processed until the job is resumed.",
      confirmText: "Yes, Pause",
      variant: "warning",
    },
    cancel: {
      title: "Cancel Warmup Job?",
      html: "<strong>This cannot be undone.</strong><br/>Messages already in the queue will be discarded.",
      confirmText: "Yes, Cancel Job",
      variant: "danger",
    },
  };

  const handleStatusAction = (
    jobId: number,
    action: "pause" | "resume" | "cancel",
  ) => {
    if (confirmConfig[action]) {
      setPendingAction({ jobId, action });
      return;
    }
    executeStatusAction(jobId, action);
  };

  const executeStatusAction = async (
    jobId: number,
    action: "pause" | "resume" | "cancel",
  ) => {
    try {
      const response = await callApi(
        `sysadmin/warmup/jobs/${jobId}/${action}`,
        HttpMethod.PUT,
      );
      toast.success(response.message || `Job ${action}d successfully`);
      loadData();
      loadCapacity();
      if (selectedJob?.warmupJobId === jobId) {
        loadJobDetail(jobId);
      }
    } catch (err: any) {
      toast.error(err.message || `Failed to ${action} job`);
    }
  };

  // --- CSV Dropzone ---

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const phones = parsePhoneNumbers(text);
        setCsvPhoneCount(phones.length);
      };
      reader.readAsText(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "text/csv": [".csv"], "text/plain": [".txt"] },
  });

  // --- Account Name Lookup ---

  const handleAcctIdBlur = async () => {
    if (!createForm.acctId) {
      setAccountNameLookup("");
      return;
    }
    try {
      const response = await callApi(
        `sysadmin/warmup/account-name/${createForm.acctId}`,
        HttpMethod.GET,
        undefined,
        true,
        undefined,
        undefined,
        undefined,
        [404],
      );
      if (response?.name) {
        setAccountNameLookup(response.name);
      } else if (response?.status === 404) {
        setAccountNameLookup("Account not found");
      } else {
        setAccountNameLookup(response?.name || "");
      }
    } catch {
      setAccountNameLookup("Account not found");
    }
  };

  // --- Create Job ---

  const handleCreateJob = async () => {
    if (!createForm.acctId || !createForm.name || !csvFile) {
      toast.error("Please fill in all required fields and upload a CSV file");
      return;
    }

    // Guard: call length must be at least 5s for every selected carrier
    const tooShort = createForm.carriers.find(
      (c) => (createForm.callDurations[c] ?? 0) < 5,
    );
    if (tooShort) {
      toast.error(`Call length for ${tooShort} must be at least 5 seconds`);
      return;
    }

    const template = RAMP_TEMPLATES[createForm.rampTemplateIndex];
    let ramp: number[];
    if (template.label === "Custom") {
      ramp = createForm.customRamp
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);
      if (ramp.length === 0) {
        toast.error(
          "Please enter a valid custom ramp schedule (comma-separated numbers)",
        );
        return;
      }
    } else {
      ramp = template.value;
    }

    setIsCreating(true);
    try {
      // Send startDate as UTC midnight of the picked date so the calendar day
      // the user selected survives the UTC conversion (otherwise toISOString()
      // shifts evening-Pacific picks into the next UTC day).
      const d = createForm.startDate;
      const startDateUtcMidnight = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()),
      ).toISOString();

      const response = await callApi(
        "sysadmin/warmup/jobs",
        HttpMethod.POST,
        {
          acctId: createForm.acctId,
          name: createForm.name,
          startDate: startDateUtcMidnight,
          rampSchedule: JSON.stringify(ramp),
          carriers: createForm.carriers.join(","),
          notificationEmail: createForm.notificationEmail.trim(),
          callDurations: JSON.stringify(
            Object.fromEntries(
              createForm.carriers.map((c) => [
                c,
                createForm.callDurations[c] ?? 7,
              ]),
            ),
          ),
        },
        true,
        undefined,
        csvFile,
      );
      toast.success(response.message || "Warmup job created");
      setShowCreateModal(false);
      resetCreateForm();
      loadData();
      loadCapacity();
    } catch (err: any) {
      toast.error(err.message || "Failed to create warmup job");
    } finally {
      setIsCreating(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      acctId: "",
      name: "",
      startDate: new Date(),
      rampTemplateIndex: 3,
      customRamp: "",
      carriers: ["Verizon"],
      callDurations: { ATT: 7, Verizon: 7, TMobile: 7 },
      notificationEmail: DEFAULT_NOTIFICATION_EMAIL,
    });
    setCsvFile(null);
    setCsvPhoneCount(0);
    setAccountNameLookup("");
  };

  // --- Current ramp preview ---

  const currentRamp = (): number[] => {
    const template = RAMP_TEMPLATES[createForm.rampTemplateIndex];
    if (template.label === "Custom") {
      return createForm.customRamp
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);
    }
    return template.value;
  };

  // --- Custom Cells ---

  const AccountCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    return (
      <td>
        <div>
          <strong>{dataItem.acctId}</strong>
          {dataItem.accountName && (
            <div className="text-muted small">{dataItem.accountName}</div>
          )}
        </div>
      </td>
    );
  };

  const StatusCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    return (
      <td>
        <span className={`badge ${getStatusBadgeClass(dataItem.status)}`}>
          {dataItem.status}
        </span>
      </td>
    );
  };

  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;
    return <td>{formatTimestamp(dataItem[field])}</td>;
  };

  const ProgressCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    if (dataItem.currentDay === 0) return <td>Not started</td>;
    return (
      <td>
        Day {dataItem.currentDay}/{dataItem.totalDays}
      </td>
    );
  };

  const ScheduleCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const ramp = parseRamp(dataItem.rampSchedule);
    if (ramp.length === 0) return <td>-</td>;
    return (
      <td>
        <span className="small">
          {ramp.map((val, i) => (
            <span key={i}>
              {i > 0 && <span className="text-muted">, </span>}
              <span
                style={
                  i + 1 === dataItem.currentDay
                    ? { fontWeight: "bold", color: "#0d6efd" }
                    : {}
                }
              >
                {val}
              </span>
            </span>
          ))}
        </span>
      </td>
    );
  };

  // Compact "V:5 A:30 T:5" renderer for the callDurations JSON column.
  // Only shows carriers that are actually selected for the job.
  const CallLengthCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const raw = dataItem.callDurations as string | null;
    if (!raw) return <td className="text-muted small">default</td>;
    let map: Record<string, number>;
    try {
      map = JSON.parse(raw);
    } catch {
      return <td className="text-muted small">-</td>;
    }
    const labels: Record<string, string> = {
      Verizon: "V",
      ATT: "A",
      TMobile: "T",
    };
    const parts = Object.entries(map).map(
      ([k, v]) => `${labels[k] ?? k}:${v}s`,
    );
    return <td className="small">{parts.join(" ")}</td>;
  };

  const TodaysCallsCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    if (dataItem.todaysTarget === 0) return <td>-</td>;
    const totalTarget = dataItem.todaysTarget * dataItem.phoneCount;
    return (
      <td>
        {dataItem.todaysActual.toLocaleString()}/{totalTarget.toLocaleString()}
        <span className="text-muted small ms-1">
          ({dataItem.todaysTarget}/num)
        </span>
      </td>
    );
  };

  const ActionsCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const jobId = dataItem.warmupJobId;
    const status = dataItem.status;

    return (
      <td style={{ overflow: "visible" }}>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleViewJob(jobId)}
            title="View Details"
          >
            <i className="bi bi-eye"></i>
          </button>
          {(status === "Scheduled" || status === "InProgress") && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleStatusAction(jobId, "pause")}
              title="Pause"
            >
              <i className="bi bi-pause-fill"></i>
            </button>
          )}
          {status === "Paused" && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleStatusAction(jobId, "resume")}
              title="Resume"
            >
              <i className="bi bi-play-fill"></i>
            </button>
          )}
          {status !== "Completed" && status !== "Cancelled" && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleStatusAction(jobId, "cancel")}
              title="Cancel"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </td>
    );
  };

  // --- Render ---

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Warmup Jobs</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetCreateForm();
            setShowCreateModal(true);
          }}
        >
          <i className="bi bi-plus-lg me-1"></i>
          New Warmup Job
        </button>
      </div>

      {/* Capacity Summary */}
      {capacity &&
        (() => {
          // Status is evaluated per carrier: each warmup number is dialed once per selected
          // carrier, so the farm runs at the slowest carrier's pace. A blended farm-wide
          // number hides one carrier being pinned while another idles.
          //
          // Observed throughput is intentionally low — the planner spreads calls randomly
          // across the day — so status compares scheduled DEMAND against device CAPACITY,
          // plus the queue backlog (calls due now but not dialing) to catch a stalled dialer.
          const carriers = capacity.byCarrier ?? [];
          const statusOf = (c: CarrierCapacity) =>
            c.callsPerMinute === 0 &&
            (c.queueBacklog != null
              ? c.queueBacklog > 0
              : c.todaysDemand > 0)
              ? "stalled"
              : c.utilizationPct >= 100
                ? "overloaded"
                : c.utilizationPct >= 80
                  ? "atCapacity"
                  : "ok";
          const overCarriers = carriers.filter((c) => statusOf(c) === "overloaded");
          const capCarriers = carriers.filter((c) => statusOf(c) === "atCapacity");
          const stalledCarriers = carriers.filter((c) => statusOf(c) === "stalled");
          const overloaded = overCarriers.length > 0;
          const atCapacity = capCarriers.length > 0;
          const stalled = stalledCarriers.length > 0;
          const names = (cs: CarrierCapacity[]) =>
            cs.map((c) => c.carrier).join(", ");
          return (
            <>
              {(overloaded || atCapacity || stalled) && (
                <div
                  className={`alert ${overloaded || stalled ? "alert-danger" : "alert-warning"} py-2 mb-3`}
                >
                  <strong>
                    <i
                      className={`bi ${overloaded || stalled ? "bi-exclamation-triangle-fill" : "bi-exclamation-circle"} me-1`}
                    ></i>
                    {stalled
                      ? `Dialer Stalled (${names(stalledCarriers)})`
                      : overloaded
                        ? `Farm Overloaded (${names(overCarriers)})`
                        : `Farm at Capacity (${names(capCarriers)})`}
                  </strong>{" "}
                  &mdash;{" "}
                  {stalled
                    ? `${names(stalledCarriers)} ${stalledCarriers.length === 1 ? "has" : "have"} calls due in the queue but none are being placed. Check that the WarmupDialer WebJob is running and devices are flagged.`
                    : overloaded
                      ? `Scheduled warmup demand exceeds what the ${names(overCarriers)} devices can dial in a day — jobs will fall behind their ramp schedule. Add devices or pause/stagger jobs.`
                      : `${names(capCarriers)} ${capCarriers.length === 1 ? "is" : "are"} above 80% of daily device capacity. New jobs may not finish on schedule; consider adding devices.`}
                </div>
              )}
              {/* Per-carrier breakdown: device supply vs scheduled demand, plus live backlog. */}
              {carriers.length > 0 && (
                <div className="card mb-3">
                  <div className="card-body py-2">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Carrier</th>
                            <th className="text-end">
                              Devices
                              <ToolTip description="Active / provisioned. e.g. 2/25 = 25 SIM-farm devices are reserved for warmup on this carrier, and 2 of them placed a call in the last 30 minutes." />
                            </th>
                            <th className="text-end">
                              Calls/min
                              <ToolTip description="Observed dialing rate. Low numbers are normal — the planner intentionally spreads each day's calls across the whole day rather than dialing as fast as possible." />
                            </th>
                            <th className="text-end">
                              Today
                              <ToolTip description="Calls scheduled for today across all in-progress warmup jobs on this carrier (today's ramp step × phone count, summed over jobs)." />
                            </th>
                            <th className="text-end">
                              Peak Day
                              <ToolTip description="The busiest single day in the next 7 days as job ramps grow. Capacity is judged against this, not today, so you see overload before it happens." />
                            </th>
                            <th className="text-end">
                              Capacity/Day
                              <ToolTip description="Maximum calls the provisioned devices can physically place in 24 hours, from each carrier's average call duration plus the per-device cooldown between calls." />
                            </th>
                            <th className="text-end">
                              Load
                              <ToolTip description="Peak Day demand as a % of Capacity/Day. 80%+ = At Capacity, 100%+ = Overloaded." />
                            </th>
                            <th className="text-end">
                              Backlog
                              <ToolTip description="Calls due now but not yet dialed (Service Bus queue depth). Should hover near 0 — a growing backlog with 0 calls/min means the dialer is stalled." />
                            </th>
                            <th className="text-end">
                              Status
                              <ToolTip description="OK: under 80% load. At Capacity: 80%+. Overloaded: 100%+ — jobs will fall behind their ramp schedule. Stalled: calls are due but none are being placed (check the WarmupDialer WebJob)." />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {carriers.map((c) => {
                            const st = statusOf(c);
                            const badge =
                              st === "overloaded" || st === "stalled"
                                ? "bg-danger"
                                : st === "atCapacity"
                                  ? "bg-warning text-dark"
                                  : "bg-success";
                            const label =
                              st === "overloaded"
                                ? "Overloaded"
                                : st === "atCapacity"
                                  ? "At Capacity"
                                  : st === "stalled"
                                    ? "Stalled"
                                    : "OK";
                            return (
                              <tr key={c.carrier}>
                                <td>
                                  <strong>{c.carrier}</strong>
                                </td>
                                <td className="text-end">
                                  {c.activeDevices}/{c.provisionedDevices}
                                </td>
                                <td className="text-end">{c.callsPerMinute}</td>
                                <td className="text-end">
                                  {c.todaysDemand.toLocaleString()}
                                </td>
                                <td className="text-end">
                                  {c.peakDailyDemand.toLocaleString()}
                                </td>
                                <td className="text-end">
                                  {c.dailyCapacity.toLocaleString()}
                                </td>
                                <td className="text-end">
                                  {c.dailyCapacity > 0 || c.peakDailyDemand > 0
                                    ? `${c.utilizationPct}%`
                                    : "—"}
                                </td>
                                <td className="text-end">
                                  {c.queueBacklog != null
                                    ? c.queueBacklog.toLocaleString()
                                    : "—"}
                                </td>
                                <td className="text-end">
                                  <span className={`badge ${badge}`}>
                                    {label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <div className="card mb-3">
                <div className="card-body py-2">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <small className="text-muted d-block">Throughput</small>
                      <strong className="fs-5">
                        {capacity.callsPerMinute}
                      </strong>
                      <span className="text-muted small"> calls/min</span>
                    </div>
                    <div className="col-auto border-start">
                      <small className="text-muted d-block">Devices</small>
                      <strong>{capacity.activeDevices}</strong>
                    </div>
                    <div className="col-auto border-start">
                      <small className="text-muted d-block">Calls Made</small>
                      <strong>
                        {capacity.totalCallsMade.toLocaleString()}
                      </strong>
                    </div>
                    <div className="col-auto border-start">
                      <small className="text-muted d-block">Remaining</small>
                      <strong>
                        {capacity.remainingCalls.toLocaleString()}
                      </strong>
                      {capacity.rampDaysRemaining > 0 && (
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          ~{capacity.rampDaysRemaining}d of ramp left
                        </div>
                      )}
                    </div>
                    <div className="col-auto border-start">
                      <small className="text-muted d-block">Active Jobs</small>
                      <strong>{capacity.activeJobs}</strong>
                      {capacity.scheduledJobs > 0 && (
                        <span className="text-muted small ms-1">
                          (+{capacity.scheduledJobs} scheduled)
                        </span>
                      )}
                    </div>
                    <div className="col-auto border-start">
                      <small className="text-muted d-block">Phones</small>
                      <strong>
                        {capacity.totalPhonesInWarmup.toLocaleString()}
                      </strong>
                    </div>
                    <div className="col border-start">
                      <small className="text-muted d-block mb-1">
                        7-Day Forecast (farm calls/day, all carriers)
                      </small>
                      <div className="d-flex gap-2">
                        {capacity.forecast.map((f) => {
                          const dayLabel = new Date(
                            f.date + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "numeric",
                            day: "numeric",
                          });
                          return (
                            <div
                              key={f.date}
                              className="text-center"
                              style={{ minWidth: 52 }}
                            >
                              <div className="small text-muted">{dayLabel}</div>
                              <div
                                className={`fw-bold ${f.projectedCalls === 0 ? "text-muted" : ""}`}
                              >
                                {f.projectedCalls.toLocaleString()}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "0.7rem" }}
                              >
                                {f.activeJobCount} job
                                {f.activeJobCount !== 1 ? "s" : ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label">Account ID</label>
              <input
                type="text"
                className="form-control"
                value={filters.acctId}
                onChange={(e) =>
                  setFilters({ ...filters, acctId: e.target.value })
                }
                placeholder="Filter by Account ID..."
              />
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <option value="">All</option>
                {WARMUP_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col">
              <button className="btn btn-primary me-2" onClick={handleSearch}>
                Search
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Grid */}
      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={jobs}
          total={total}
          pageable={{ buttonCount: 5, pageSizes: [25, 50, 100] }}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          resizable={true}
          style={{ height: "calc(100vh - 280px)" }}
        >
          <Column field="warmupJobId" title="ID" width={70} />
          <Column
            field="acctId"
            title="Account"
            minResizableWidth={120}
            cell={AccountCell}
          />
          <Column field="name" title="Name" minResizableWidth={150} />
          <Column field="phoneCount" title="Phones" width={80} />
          <Column
            field="startDate"
            title="Start Date"
            minResizableWidth={120}
            cell={DateCell}
          />
          <Column
            field="rampSchedule"
            title="Schedule"
            minResizableWidth={150}
            cell={ScheduleCell}
            sortable={false}
          />
          <Column
            field="currentDay"
            title="Progress"
            minResizableWidth={100}
            cell={ProgressCell}
            sortable={false}
          />
          <Column
            field="todaysTarget"
            title="Today's Calls"
            minResizableWidth={110}
            cell={TodaysCallsCell}
            sortable={false}
          />
          <Column field="carriers" title="Carriers" minResizableWidth={100} />
          <Column
            field="callDurations"
            title="Call Length"
            minResizableWidth={110}
            cell={CallLengthCell}
            sortable={false}
          />
          <Column
            field="status"
            title="Status"
            minResizableWidth={100}
            cell={StatusCell}
          />
          <Column
            field="createdOn"
            title="Created"
            minResizableWidth={120}
            cell={DateCell}
          />
          <Column
            title="Actions"
            width={150}
            cell={ActionsCell}
            sortable={false}
          />
        </Grid>
      </div>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Warmup Job</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Account ID *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={createForm.acctId}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, acctId: e.target.value })
                      }
                      onBlur={handleAcctIdBlur}
                      placeholder="e.g. 12345"
                    />
                    {accountNameLookup && (
                      <small
                        className={
                          accountNameLookup === "Account not found"
                            ? "text-danger"
                            : "text-muted"
                        }
                      >
                        {accountNameLookup}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Job Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={createForm.name}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, name: e.target.value })
                      }
                      placeholder="e.g. Acme Corp Q1 Warmup"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="warmup-notify-email">
                      Notify on completion
                    </label>
                    <input
                      id="warmup-notify-email"
                      type="email"
                      className="form-control"
                      value={createForm.notificationEmail}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          notificationEmail: e.target.value,
                        })
                      }
                      placeholder="remediation@dnc.com"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Start Date *</label>
                    <div>
                      <DatePicker
                        selected={createForm.startDate}
                        onChange={(date) =>
                          date &&
                          setCreateForm({ ...createForm, startDate: date })
                        }
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        minDate={new Date()}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Ramp Template *</label>
                    <select
                      className="form-select"
                      value={createForm.rampTemplateIndex}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          rampTemplateIndex: parseInt(e.target.value),
                        })
                      }
                    >
                      {RAMP_TEMPLATES.map((t, i) => (
                        <option key={i} value={i}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Carriers *</label>
                    <div className="d-flex gap-3">
                      {[
                        { value: "Verizon", label: "Verizon" },
                        { value: "ATT", label: "AT&T" },
                        { value: "TMobile", label: "T-Mobile" },
                      ].map((c) => (
                        <div className="form-check" key={c.value}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`carrier-${c.value}`}
                            checked={createForm.carriers.includes(c.value)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...createForm.carriers, c.value]
                                : createForm.carriers.filter(
                                    (x) => x !== c.value,
                                  );
                              setCreateForm({
                                ...createForm,
                                carriers: updated,
                              });
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`carrier-${c.value}`}
                          >
                            {c.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap small text-muted">
                      <span className="fw-semibold">Call Length</span>
                      {[
                        { value: "Verizon", label: "Verizon" },
                        { value: "ATT", label: "AT&T" },
                        { value: "TMobile", label: "T-Mobile" },
                      ].map((c) => {
                        const selected = createForm.carriers.includes(c.value);
                        return (
                          <span
                            key={c.value}
                            className={`d-inline-flex align-items-center gap-1 ${selected ? "" : "opacity-50"}`}
                          >
                            {c.label}:
                            <input
                              type="number"
                              min={5}
                              max={120}
                              className="form-control form-control-sm d-inline-block"
                              style={{ width: "60px" }}
                              disabled={!selected}
                              value={createForm.callDurations[c.value]}
                              // Allow any keystroke while typing; clamp to >= 5 on blur
                              // so the user isn't fighting the input mid-edit
                              onChange={(e) =>
                                setCreateForm({
                                  ...createForm,
                                  callDurations: {
                                    ...createForm.callDurations,
                                    [c.value]: parseInt(e.target.value) || 0,
                                  },
                                })
                              }
                              onBlur={(e) => {
                                const val = parseInt(e.target.value) || 0;
                                if (val < 5) {
                                  setCreateForm({
                                    ...createForm,
                                    callDurations: {
                                      ...createForm.callDurations,
                                      [c.value]: 5,
                                    },
                                  });
                                }
                              }}
                            />
                            s
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {RAMP_TEMPLATES[createForm.rampTemplateIndex].label ===
                    "Custom" && (
                    <div className="col-12">
                      <label className="form-label">
                        Custom Ramp (comma-separated calls per day)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={createForm.customRamp}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            customRamp: e.target.value,
                          })
                        }
                        placeholder="e.g. 3,5,10,15,20,30"
                      />
                    </div>
                  )}
                  <div className="col-12">
                    {(() => {
                      const ramp = currentRamp();
                      const carriers = createForm.carriers.length;
                      const totalRampCalls = ramp.reduce((a, b) => a + b, 0);
                      const totalActualCalls = totalRampCalls * carriers;
                      const peakDay = ramp.length > 0 ? Math.max(...ramp) : 0;
                      return (
                        <div className="alert alert-info py-2 mb-0">
                          <strong>Ramp Preview:</strong>{" "}
                          {formatRampPreview(ramp) || "No schedule configured"}
                          {ramp.length > 0 && (
                            <div className="mt-1 small">
                              <strong>Volume Impact:</strong> {totalRampCalls}{" "}
                              scheduled calls &times; {carriers} carrier
                              {carriers === 1 ? "" : "s"} ={" "}
                              <strong>{totalActualCalls} total calls</strong>{" "}
                              over {ramp.length} days &middot; Peak day:{" "}
                              {peakDay} &times; {carriers} ={" "}
                              <strong>{peakDay * carriers} calls/day</strong>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="col-12">
                    <label className="form-label">Phone Numbers CSV *</label>
                    <div
                      {...getRootProps()}
                      className={`border rounded p-4 text-center ${isDragActive ? "border-primary bg-light" : "border-dashed"}`}
                      style={{
                        cursor: "pointer",
                        borderStyle: csvFile ? "solid" : "dashed",
                      }}
                    >
                      <input {...getInputProps()} />
                      {csvFile ? (
                        <div>
                          <i className="bi bi-file-earmark-check text-success fs-4"></i>
                          <div className="mt-1">
                            <strong>{csvFile.name}</strong>
                            <span className="text-muted ms-2">
                              ({csvPhoneCount} phone numbers)
                            </span>
                          </div>
                          <small className="text-muted">
                            Click or drag to replace
                          </small>
                        </div>
                      ) : (
                        <div>
                          <i className="bi bi-cloud-arrow-up fs-4 text-muted"></i>
                          <div className="mt-1">
                            {isDragActive
                              ? "Drop file here..."
                              : "Drag & drop CSV file, or click to browse"}
                          </div>
                          <small className="text-muted">
                            One phone number per line, 10-digit format
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="alert alert-warning py-2 mb-0">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      All phone numbers must exist in TrustCall for the
                      specified account. Numbers not in TrustCall will cause the
                      job to fail.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateJob}
                  disabled={
                    isCreating ||
                    !createForm.acctId ||
                    !createForm.name ||
                    !csvFile
                  }
                >
                  {isCreating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Creating...
                    </>
                  ) : (
                    "Create Job"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Detail Modal */}
      {showDetailModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="modal-dialog modal-dialog-scrollable"
            style={{ maxWidth: "95vw", height: "95vh" }}
          >
            <div
              className="modal-content"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title">
                  Job Details
                  {selectedJob && (
                    <small className="text-muted ms-2">
                      #{selectedJob.warmupJobId} - {selectedJob.name}
                    </small>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDetailModal}
                ></button>
              </div>
              <div
                className="modal-body"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {isLoadingDetail && !selectedJob && (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border text-primary spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-2">Loading job details...</span>
                  </div>
                )}
                {isLoadingDetail && selectedJob && (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 16,
                      zIndex: 10,
                    }}
                  >
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    />
                  </div>
                )}

                {selectedJob && (
                  <>
                    {/* Job summary cards */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-3">
                        <div className="card bg-light">
                          <div className="card-body py-2">
                            <small className="text-muted">Account</small>
                            <div>
                              <strong>{selectedJob.acctId}</strong>
                              {selectedJob.accountName && (
                                <div className="small text-muted">
                                  {selectedJob.accountName}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-light">
                          <div className="card-body py-2">
                            <small className="text-muted">Status</small>
                            <div>
                              <span
                                className={`badge ${getStatusBadgeClass(selectedJob.status)}`}
                              >
                                {selectedJob.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-light">
                          <div className="card-body py-2">
                            <small className="text-muted">Progress</small>
                            <div>
                              <strong>
                                Day {selectedJob.currentDay}/
                                {selectedJob.totalDays}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="card bg-light">
                          <div className="card-body py-2">
                            <small className="text-muted">Phone Numbers</small>
                            <div>
                              <strong>{selectedJob.phoneCount}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="card bg-light">
                          <div className="card-body py-2">
                            <small className="text-muted">Today's Calls</small>
                            <div>
                              <strong>
                                {selectedJob.todaysTarget > 0
                                  ? `${selectedJob.todaysActual.toLocaleString()} / ${(selectedJob.todaysTarget * selectedJob.phoneCount).toLocaleString()}`
                                  : "-"}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ramp schedule + Action buttons */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <small className="text-muted">Ramp Schedule:</small>{" "}
                        <span className="small">
                          {formatRampPreview(
                            parseRamp(selectedJob.rampSchedule),
                          )}
                        </span>
                      </div>
                      <div>
                        {(selectedJob.status === "Scheduled" ||
                          selectedJob.status === "InProgress") && (
                          <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={() =>
                              handleStatusAction(
                                selectedJob.warmupJobId,
                                "pause",
                              )
                            }
                          >
                            <i className="bi bi-pause-fill me-1"></i>Pause
                          </button>
                        )}
                        {selectedJob.status === "Paused" && (
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() =>
                              handleStatusAction(
                                selectedJob.warmupJobId,
                                "resume",
                              )
                            }
                          >
                            <i className="bi bi-play-fill me-1"></i>Resume
                          </button>
                        )}
                        {selectedJob.status !== "Completed" &&
                          selectedJob.status !== "Cancelled" && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                handleStatusAction(
                                  selectedJob.warmupJobId,
                                  "cancel",
                                )
                              }
                            >
                              <i className="bi bi-x-lg me-1"></i>Cancel
                            </button>
                          )}
                      </div>
                    </div>

                    {/* Tabs: Numbers / Call Logs */}
                    <ul className="nav nav-tabs">
                      <li className="nav-item">
                        <button
                          className={`nav-link ${detailTab === "numbers" ? "active" : ""}`}
                          onClick={() => setDetailTab("numbers")}
                        >
                          Phone Numbers ({totalNumbers})
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link ${detailTab === "logs" ? "active" : ""}`}
                          onClick={() => {
                            setDetailTab("logs");
                            if (callLogs.length === 0 && selectedJob)
                              loadCallLogs(selectedJob.warmupJobId);
                          }}
                        >
                          Call Logs ({totalLogs})
                        </button>
                      </li>
                    </ul>

                    <div
                      className="border border-top-0 p-3"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      {/* Numbers Tab */}
                      {detailTab === "numbers" && (
                        <>
                          {jobNumbers.length === 0 && !isLoadingDetail && (
                            <div className="alert alert-info">
                              No phone numbers found.
                            </div>
                          )}
                          {jobNumbers.length > 0 && (
                            <Grid
                              data={jobNumbers}
                              total={totalNumbers}
                              pageable={{
                                buttonCount: 5,
                                pageSizes: [25, 50, 100],
                              }}
                              skip={numbersPage.skip}
                              take={numbersPage.take}
                              onPageChange={(e: GridPageChangeEvent) => {
                                setNumbersPage({
                                  skip: e.page.skip,
                                  take: e.page.take,
                                });
                              }}
                              filterable={true}
                              filter={numbersFilter}
                              onFilterChange={(e: GridFilterChangeEvent) => {
                                setNumbersFilter(e.filter);
                                setNumbersPage((p) => ({ ...p, skip: 0 }));
                              }}
                              resizable={true}
                              style={{ height: "calc(95vh - 340px)" }}
                            >
                              <Column
                                field="phoneNumber"
                                title="Phone Number"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    {formatPhone(props.dataItem.phoneNumber)}
                                  </td>
                                )}
                              />
                              <Column
                                field="status"
                                title="Status"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    <span
                                      className={`badge ${props.dataItem.status === "Active" ? "bg-success" : props.dataItem.status === "Pending" ? "bg-warning text-dark" : "bg-secondary"}`}
                                    >
                                      {props.dataItem.status}
                                    </span>
                                  </td>
                                )}
                              />
                              <Column
                                field="callsToday"
                                title="Calls Today"
                                filter="numeric"
                              />
                              <Column
                                field="totalCalls"
                                title="Total Calls"
                                filter="numeric"
                              />
                              <Column
                                field="lastCalledOn"
                                title="Last Called"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    {formatTimestamp(
                                      props.dataItem.lastCalledOn,
                                    )}
                                  </td>
                                )}
                              />
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Call Logs Tab */}
                      {detailTab === "logs" && (
                        <>
                          {isLoadingLogs && (
                            <div className="text-center py-3">
                              <div className="spinner-border spinner-border-sm text-primary"></div>
                              <span className="ms-2">Loading call logs...</span>
                            </div>
                          )}
                          {!isLoadingLogs && callLogs.length === 0 && (
                            <div className="alert alert-info">
                              No call logs yet.
                            </div>
                          )}
                          {callLogs.length > 0 && (
                            <Grid
                              data={callLogs}
                              total={totalLogs}
                              pageable={{
                                buttonCount: 5,
                                pageSizes: [25, 50, 100],
                              }}
                              skip={logsPage.skip}
                              take={logsPage.take}
                              onPageChange={(e: GridPageChangeEvent) => {
                                setLogsPage({
                                  skip: e.page.skip,
                                  take: e.page.take,
                                });
                              }}
                              filterable={true}
                              filter={logsFilter}
                              onFilterChange={(e: GridFilterChangeEvent) => {
                                setLogsFilter(e.filter);
                                setLogsPage((p) => ({ ...p, skip: 0 }));
                              }}
                              resizable={true}
                              style={{ height: "calc(95vh - 340px)" }}
                            >
                              <Column
                                field="phoneNumber"
                                title="Phone"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    {formatPhone(props.dataItem.phoneNumber)}
                                  </td>
                                )}
                              />
                              <Column
                                field="devicePhoneNumber"
                                title="Device"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    {formatPhone(
                                      props.dataItem.devicePhoneNumber,
                                    )}
                                  </td>
                                )}
                              />
                              <Column field="carrier" title="Carrier" />
                              <Column
                                field="calledOn"
                                title="Called On"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    {formatTimestamp(props.dataItem.calledOn)}
                                  </td>
                                )}
                              />
                              <Column
                                field="callDurationSecs"
                                title="Duration"
                                filter="numeric"
                                cell={(props: GridCellProps) => (
                                  <td>{props.dataItem.callDurationSecs}s</td>
                                )}
                              />
                              <Column
                                field="success"
                                title="Success"
                                cell={(props: GridCellProps) => (
                                  <td>
                                    <span
                                      className={`badge ${props.dataItem.success ? "bg-success" : "bg-danger"}`}
                                    >
                                      {props.dataItem.success ? "Yes" : "No"}
                                    </span>
                                  </td>
                                )}
                              />
                              <Column
                                field="asteriskResponse"
                                title="Response"
                                cell={(props: GridCellProps) => {
                                  const val =
                                    props.dataItem.asteriskResponse || "-";
                                  const [expanded, setExpanded] =
                                    React.useState(false);
                                  if (val === "-") return <td>-</td>;
                                  let pretty = val;
                                  try {
                                    pretty = JSON.stringify(
                                      JSON.parse(val),
                                      null,
                                      2,
                                    );
                                  } catch {
                                    /* not JSON */
                                  }
                                  const summary =
                                    val.length > 50
                                      ? `${val.substring(0, 50)}...`
                                      : val;
                                  return (
                                    <td>
                                      {expanded ? (
                                        <div>
                                          <span
                                            style={{
                                              cursor: "pointer",
                                              color: "#0d6efd",
                                            }}
                                            onClick={() => setExpanded(false)}
                                          >
                                            <i
                                              className="bi bi-chevron-up me-1"
                                              style={{ fontSize: "0.7rem" }}
                                            ></i>
                                            Collapse
                                          </span>
                                          <pre
                                            className="mt-1 mb-0 p-2 bg-light rounded"
                                            style={{
                                              fontSize: "0.75rem",
                                              whiteSpace: "pre-wrap",
                                              maxHeight: 200,
                                              overflow: "auto",
                                            }}
                                          >
                                            {pretty}
                                          </pre>
                                        </div>
                                      ) : (
                                        <span
                                          style={{
                                            cursor: "pointer",
                                            color: "#0d6efd",
                                          }}
                                          onClick={() => setExpanded(true)}
                                        >
                                          {summary}
                                          {val.length > 50 && (
                                            <i
                                              className="bi bi-chevron-down ms-1"
                                              style={{ fontSize: "0.7rem" }}
                                            ></i>
                                          )}
                                        </span>
                                      )}
                                    </td>
                                  );
                                }}
                              />
                            </Grid>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDetailModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {pendingAction && confirmConfig[pendingAction.action] && (
        <ConfirmationModal
          isVisible={true}
          title={confirmConfig[pendingAction.action].title}
          htmlMessage={confirmConfig[pendingAction.action].html}
          confirmText={confirmConfig[pendingAction.action].confirmText}
          confirmVariant={confirmConfig[pendingAction.action].variant}
          cancelText="No, Go Back"
          onConfirm={() => {
            const { jobId, action } = pendingAction;
            setPendingAction(null);
            executeStatusAction(jobId, action);
          }}
          onCancel={() => setPendingAction(null)}
        />
      )}
    </div>
  );
};

export default WarmupJobs;
